<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';

const PLAN_KEYS = ['STARTER', 'BASIC', 'PRO', 'ENTERPRISE'];
const FEATURE_COUNT = 7;

export default {
  name: 'OnboardingPlanStep',
  components: { Spinner },
  props: {
    // Rendered outside the onboarding wizard (the pricing table page for an
    // inactive subscription): no wizard "Continue" button and its own copy.
    standalone: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      switchingPlan: null,
      // 'monthly' | 'annual' — the values the BE takes.
      interval: 'monthly',
      isLoading: true,
      loadFailed: false,
      // Set when the BE says this subscription cannot be switched online
      // (currency_mismatch). Stops further attempts rather than offering a retry.
      switchBlocked: false,
      // Prices and the list of plans on sale come only from /stripe/plans —
      // nothing is rendered from bundled numbers if it fails.
      apiPlans: [],
      // The live subscription as the BE reports it: { plan, interval, status }
      // or null. Replaces matching the entitlement's plan_name, which has no
      // interval.
      current: null,
    };
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
    }),
    accountId() {
      return this.$route.params.accountId;
    },
    // The "excludes tax" line is not shown on SingaBot. accountSource is
    // derived from the request domain on the server.
    showTaxNote() {
      return !this.loadFailed && this.globalConfig.accountSource !== 'singabot';
    },
    currentPlanKey() {
      return this.current?.plan ? this.current.plan.toUpperCase() : null;
    },
    plans() {
      // A plan the brand does not sell is absent from the API list, so only
      // what it returns is rendered.
      return this.apiPlans
        .filter(plan => PLAN_KEYS.includes(plan.key.toUpperCase()))
        .map(plan => {
          const key = plan.key.toUpperCase();
          const base = `ONBOARDING_SHELL.STEPS.PLAN.PLANS.${key}`;
          const apiFeatures = plan.features || [];
          return {
            key,
            name: this.$t(`${base}.NAME`),
            price: this.formatPrice(plan.amount, plan.currency),
            description: this.$t(`${base}.DESCRIPTION`),
            tagline: this.$t(`${base}.TAGLINE`),
            // Staging Stripe products have no marketing features, so an empty
            // list falls back to the copy bundled here.
            features: apiFeatures.length
              ? apiFeatures
              : Array.from({ length: FEATURE_COUNT }, (_, i) =>
                  this.$t(`${base}.FEATURES.${i}`, {
                    brand: this.globalConfig.installationName,
                  })
                ),
            // Current means the same plan on the same interval. The same plan
            // on the other tab is a valid switch (monthly <-> yearly).
            isCurrent:
              this.currentPlanKey === key &&
              this.current.interval === this.interval,
            isPopular: key === 'PRO',
          };
        });
    },
    // Names the subscribed plan on either tab, for the trial note.
    currentPlanLabel() {
      const plan = this.plans.find(p => p.key === this.currentPlanKey);
      return plan ? plan.name : '';
    },
    // Built from the plans response alone (status, days_remaining), so it does
    // not wait on the entitlement. A plan the brand doesn't list (legacy
    // price) has no name to show, so the note drops it.
    trialNote() {
      const current = this.current;
      if (!current || current.status !== 'trialing') return '';
      if (!Number.isFinite(current.days_remaining)) return '';
      const days = current.days_remaining;
      return this.currentPlanLabel
        ? this.$t('ONBOARDING_SHELL.STEPS.PLAN.TRIAL_NOTE', {
            plan: this.currentPlanLabel,
            days,
          })
        : this.$t('ONBOARDING_SHELL.STEPS.PLAN.TRIAL_NOTE_GENERIC', { days });
    },
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    ...mapActions('stripe', ['fetchPlanSession', 'fetchPlans']),
    async loadPlans() {
      try {
        const response = await this.fetchPlans({
          id: this.accountId,
          interval: this.interval,
        });
        // A plan without a usable amount is dropped rather than shown as NaN.
        const plans = response.plans
          .map(plan => ({ ...plan, amount: Number(plan.price) }))
          .filter(plan => Number.isFinite(plan.amount));
        this.apiPlans = plans;
        this.current = response.current;
        // An empty catalog is as unusable as a failed call.
        this.loadFailed = !plans.length;
      } catch (error) {
        this.loadFailed = true;
      }
    },
    async selectInterval(interval) {
      if (interval === this.interval || this.isLoading) return;
      this.interval = interval;
      this.isLoading = true;
      this.loadFailed = false;
      await this.loadPlans();
      this.isLoading = false;
    },
    // One call: the plans response carries the prices, the current plan and
    // the trial, so nothing pops in after the cards are on screen.
    async loadPage() {
      this.isLoading = true;
      this.loadFailed = false;
      await this.loadPlans();
      this.isLoading = false;
    },
    formatPrice(amount, currency) {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        // Whole amounts stay "RM 39"; anything else always shows both
        // decimals ("RM 421.20", not "RM 421.2").
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    async handleSwitch(plan) {
      if (this.switchingPlan) return;
      this.switchingPlan = plan.key;
      try {
        // Onboarding ends on its own "all set" page; a renewal from the pricing
        // page goes back to the dashboard instead.
        const returnPath = this.standalone
          ? 'dashboard'
          : 'onboarding_shell/complete';
        const returnUrl = `${window.location.origin}/app/accounts/${this.accountId}/${returnPath}`;
        const url = await this.fetchPlanSession({
          plan: plan.key.toLowerCase(),
          interval: this.interval,
          returnUrl,
        });
        window.location.assign(url);
      } catch (error) {
        const code = error?.response?.data?.message;
        if (code === 'currency_mismatch') {
          // Stripe can't change a subscription's currency, so neither the
          // portal nor Checkout can do this switch. No retry is offered.
          this.switchBlocked = true;
          useAlert(this.$t('ONBOARDING_SHELL.STEPS.PLAN.CURRENCY_MISMATCH'));
        } else if (code === 'already_on_plan') {
          // Stale page or a double click — not an error. Re-read the live
          // subscription so the card shows as current.
          useAlert(this.$t('ONBOARDING_SHELL.STEPS.PLAN.ALREADY_ON_PLAN'));
          this.loadPlans();
        } else {
          useAlert(this.$t('ONBOARDING_SHELL.STEPS.PLAN.SWITCH_ERROR'));
        }
      } finally {
        this.switchingPlan = null;
      }
    },
    handleContinue() {
      this.$router.push({
        name: 'onboarding_complete',
        params: { accountId: this.accountId },
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-3 p-4 pb-24 md:px-10 md:pt-4 w-full">
    <div class="flex flex-col gap-1 text-center">
      <h2 class="text-xl font-medium text-slate-900 dark:text-slate-100">
        {{ $t('ONBOARDING_SHELL.STEPS.PLAN.HEADING') }}
      </h2>
      <span class="text-sm text-slate-600 dark:text-slate-400">
        {{
          standalone
            ? $t('ONBOARDING_SHELL.STEPS.PLAN.STANDALONE_SUBHEADING')
            : $t('ONBOARDING_SHELL.STEPS.PLAN.SUBHEADING')
        }}
      </span>
      <span
        v-if="!isLoading && trialNote"
        data-testid="onboarding_plan_trial_note"
        class="text-sm font-medium text-woot-600 dark:text-woot-400"
      >
        {{ trialNote }}
      </span>
    </div>

    <div class="flex justify-center">
      <div
        data-testid="onboarding_plan_interval_toggle"
        class="inline-flex p-1 gap-1 rounded-full bg-slate-100 dark:bg-slate-800"
      >
        <button
          type="button"
          data-testid="onboarding_plan_interval_monthly"
          class="px-4 py-1 text-sm font-medium rounded-full"
          :class="
            interval === 'monthly'
              ? 'bg-woot-500 text-white'
              : 'text-slate-600 dark:text-slate-300'
          "
          :disabled="isLoading"
          @click="selectInterval('monthly')"
        >
          {{ $t('ONBOARDING_SHELL.STEPS.PLAN.INTERVAL_MONTHLY') }}
        </button>
        <button
          type="button"
          data-testid="onboarding_plan_interval_annual"
          class="flex items-center gap-2 px-4 py-1 text-sm font-medium rounded-full"
          :class="
            interval === 'annual'
              ? 'bg-woot-500 text-white'
              : 'text-slate-600 dark:text-slate-300'
          "
          :disabled="isLoading"
          @click="selectInterval('annual')"
        >
          {{ $t('ONBOARDING_SHELL.STEPS.PLAN.INTERVAL_YEARLY') }}
          <span
            class="px-2 py-0.5 text-xs rounded-full bg-woot-50 text-woot-600"
          >
            {{ $t('ONBOARDING_SHELL.STEPS.PLAN.SAVE_PERCENT') }}
          </span>
        </button>
      </div>
    </div>

    <div
      v-if="switchBlocked"
      data-testid="onboarding_plan_switch_blocked"
      class="max-w-xl mx-auto px-4 py-2 text-sm text-center rounded-lg bg-woot-50 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
    >
      {{ $t('ONBOARDING_SHELL.STEPS.PLAN.CURRENCY_MISMATCH') }}
    </div>

    <div
      v-if="isLoading"
      data-testid="onboarding_plan_loading"
      class="flex justify-center py-16"
    >
      <Spinner />
    </div>

    <div
      v-else-if="loadFailed"
      data-testid="onboarding_plan_error"
      class="flex flex-col items-center gap-3 py-12 text-center"
    >
      <span class="text-lg font-medium text-slate-900 dark:text-slate-100">
        {{ $t('ONBOARDING_SHELL.STEPS.PLAN.LOAD_ERROR_HEADING') }}
      </span>
      <span class="max-w-md text-sm text-slate-600 dark:text-slate-400">
        {{ $t('ONBOARDING_SHELL.STEPS.PLAN.LOAD_ERROR_BODY') }}
      </span>
      <woot-button
        data-testid="onboarding_plan_retry_button"
        variant="smooth"
        @click="loadPage"
      >
        {{ $t('ONBOARDING_SHELL.STEPS.PLAN.RETRY') }}
      </woot-button>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <div
        v-for="plan in plans"
        :key="plan.key"
        :data-testid="`onboarding_plan_card_${plan.key.toLowerCase()}`"
        class="relative flex flex-col gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border-2"
        :class="
          plan.isCurrent
            ? 'border-woot-500'
            : 'border-slate-100 dark:border-slate-700'
        "
      >
        <span
          v-if="plan.isPopular"
          class="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-xs font-medium rounded-full bg-woot-50 text-woot-600 border border-woot-200"
        >
          {{ $t('ONBOARDING_SHELL.STEPS.PLAN.MOST_POPULAR') }}
        </span>

        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <span
              class="text-lg font-medium text-slate-900 dark:text-slate-100"
            >
              {{ plan.name }}
            </span>
            <span
              v-if="plan.isCurrent"
              data-testid="onboarding_plan_current_pill"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-woot-500 text-white"
            >
              {{ $t('ONBOARDING_SHELL.STEPS.PLAN.CURRENT_PLAN') }}
            </span>
          </div>
          <div class="flex items-baseline gap-1">
            <span class="text-3xl font-bold text-slate-900 dark:text-slate-100">
              {{ plan.price }}
            </span>
            <span class="text-sm text-slate-600 dark:text-slate-400">
              {{
                interval === 'annual'
                  ? $t('ONBOARDING_SHELL.STEPS.PLAN.PER_YEAR')
                  : $t('ONBOARDING_SHELL.STEPS.PLAN.PER_MONTH')
              }}
            </span>
          </div>
          <span
            v-if="interval === 'annual'"
            :data-testid="`onboarding_plan_discount_${plan.key.toLowerCase()}`"
            class="text-xs font-medium text-woot-600 dark:text-woot-400"
          >
            {{ $t('ONBOARDING_SHELL.STEPS.PLAN.BILLED_ANNUALLY') }}
          </span>
          <!-- Three lines of room from md up, so a longer description on one
               card does not push its Switch button below the others. -->
          <span
            class="text-sm text-slate-600 dark:text-slate-400 md:min-h-[3.75rem]"
          >
            {{ plan.description }}
          </span>
        </div>

        <woot-button
          :data-testid="`onboarding_plan_switch_${plan.key.toLowerCase()}`"
          class="w-full justify-center"
          :variant="plan.isCurrent ? 'smooth' : 'solid'"
          :is-disabled="plan.isCurrent || switchBlocked"
          :is-loading="switchingPlan === plan.key"
          @click="handleSwitch(plan)"
        >
          {{
            plan.isCurrent
              ? $t('ONBOARDING_SHELL.STEPS.PLAN.CURRENT_PLAN')
              : $t(
                  standalone
                    ? 'ONBOARDING_SHELL.STEPS.PLAN.SUBSCRIBE_TO'
                    : 'ONBOARDING_SHELL.STEPS.PLAN.SWITCH_TO',
                  { plan: plan.name }
                )
          }}
        </woot-button>

        <div class="flex flex-col gap-1 flex-1">
          <div
            v-for="feature in plan.features"
            :key="feature"
            class="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300"
          >
            <fluent-icon
              icon="checkmark"
              size="14"
              class="mt-0.5 flex-shrink-0 text-woot-500"
            />
            <span>{{ feature }}</span>
          </div>
        </div>

        <span class="text-xs italic text-slate-600 dark:text-slate-400">{{
          plan.tagline
        }}</span>
      </div>
    </div>

    <!-- Pinned to the bottom of the screen so Continue and the tax note stay
         in view however tall the cards make the page. -->
    <div
      v-if="!isLoading"
      class="fixed bottom-0 inset-x-0 z-10 flex flex-col items-center gap-1 px-4 py-2 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
    >
      <span
        v-if="showTaxNote"
        data-testid="onboarding_plan_tax_note"
        class="text-xs text-center text-slate-600 dark:text-slate-400"
      >
        {{ $t('ONBOARDING_SHELL.STEPS.PLAN.TAX_NOTE') }}
      </span>
      <woot-button
        v-if="!standalone"
        data-testid="onboarding_plan_continue_button"
        @click="handleContinue"
      >
        {{ $t('ONBOARDING_SHELL.STEPS.PLAN.CONTINUE') }}
      </woot-button>
    </div>
  </div>
</template>
